import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useContext } from 'react';
import api from '../services/api';
import { Printer, ArrowLeft, CheckCircle2, RotateCcw, MessageCircle, Mail, Share2 } from 'lucide-react';
import Barcode from 'react-barcode';
import { AuthContext } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import RefundModal from '../components/RefundModal';

export default function ReceiptPage() {
   const { saleId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { settings, currencySymbol } = useSettings();
  const [sale, setSale] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const taxRatePercent = parseFloat(settings.tax_rate) || 0;

  const fetchSale = () => {
    api.get(`/receipt/${saleId}`).then(res => setSale(res.data)).catch(console.error);
  };

  useEffect(() => { 
    fetchSale(); 
  }, [saleId]);
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('autoprint') === 'true' && sale) {
      // Small delay to allow layout to settle
      const timer = setTimeout(() => {
        window.print();
        // Remove the query param to avoid re-printing on manual refresh
        window.history.replaceState({}, '', window.location.pathname);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [sale]);

  const handleWhatsApp = () => {
    const text = `Hi! Here is your receipt from ${sale.store?.name || 'our store'}. Total: ${currencySymbol}${sale.total.toFixed(2)}. View it here: ${window.location.href}`;
    const phone = sale.customer?.phone ? sale.customer.phone.replace(/\D/g, '') : '';
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleEmail = async () => {
    const email = sale.customer?.email || prompt('Enter customer email:');
    if (!email) return;
    try {
      await api.post(`/receipt/${saleId}/email`, { email });
      alert('Receipt sent to email successfully!');
    } catch (err) {
      alert('Error sending email');
    }
  };

  if (!sale) {
    return (
      <div className="flex items-center justify-center h-full animate-pulse text-brand font-black text-xs uppercase tracking-widest">
        Loading receipt details...
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in-up pb-12">
      <style type="text/css" media="print">
        {`
          @page {
            size: 80mm auto;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            background-color: white;
            -webkit-print-color-adjust: exact;
          }
          #printable-receipt {
            width: 80mm !important;
            max-width: 80mm !important;
            padding: 4mm !important;
            margin: 0 auto !important;
            font-family: 'Courier New', Courier, monospace !important;
            font-size: 11pt !important;
            line-height: 1.2 !important;
            color: black !important;
          }
          .print-hidden { display: none !important; }
          .receipt-divider { border-top: 1px dashed black !important; margin: 3mm 0 !important; }
          * { box-shadow: none !important; }
        `}
      </style>
      {/* Action Bar (Hidden when printing) */}
      <div className="flex justify-between items-center bg-theme-card p-4 rounded-2xl shadow-soft border border-theme print:hidden">
        <button 
          onClick={() => navigate('/app/pos')}
          className="flex items-center gap-2 text-theme-muted hover:text-brand font-bold px-4 py-2 rounded-xl transition-all hover:bg-brand/10 text-sm"
        >
          <ArrowLeft size={18} /> New Sale
        </button>
        <div className="flex gap-3">
          {user?.role === 'admin' && (
            <button 
              onClick={() => setShowRefundModal(true)} 
              className="flex items-center gap-2 bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white font-black px-6 py-2.5 rounded-xl transition-all border border-rose-500/20 text-xs uppercase tracking-widest"
            >
              <RotateCcw size={18} /> Refund
            </button>
          )}
          <button 
            onClick={handleWhatsApp} 
            className="flex items-center gap-2 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500 hover:text-white font-black px-4 py-2.5 rounded-xl transition-all border border-emerald-500/20 text-[10px] uppercase tracking-widest"
          >
            <MessageCircle size={18} /> WhatsApp
          </button>
          <button 
            onClick={handleEmail} 
            className="flex items-center gap-2 bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500 hover:text-white font-black px-4 py-2.5 rounded-xl transition-all border border-indigo-500/20 text-[10px] uppercase tracking-widest"
          >
            <Mail size={18} /> Email
          </button>
          <button 
            onClick={() => window.print()} 
            className="flex items-center gap-2 bg-brand hover:bg-brand-light text-white font-black px-6 py-2.5 rounded-xl transition-all shadow-lg text-[10px] uppercase tracking-widest"
          >
            <Printer size={18} /> Print
          </button>
        </div>
      </div>

      {/* Receipt Container */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden print:shadow-none print:border-none print:rounded-none">
        
        {/* Success Banner */}
        <div className="bg-emerald-50 border-b border-emerald-100 p-6 flex flex-col items-center justify-center text-center print:hidden">
          <div className="bg-emerald-100 text-emerald-600 p-3 rounded-full mb-3">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-xl font-bold text-emerald-800">Payment Successful</h2>
          <p className="text-emerald-600 text-sm mt-1">Thank you for your purchase</p>
        </div>

        {/* Printable Receipt Area */}
        <div id="printable-receipt" className="p-8 max-w-[400px] mx-auto print:w-[80mm] print:max-w-[80mm] print:p-0 print:mx-0 font-mono text-sm text-gray-800">
          
          {/* Store Header */}
          <div className="text-center mb-6">
            {settings.store_logo && (
              <img src={settings.store_logo} alt="Store Logo" className="h-14 mx-auto mb-4 object-contain print:h-12" />
            )}
            <h1 className="text-2xl font-black mb-1 print:text-xl">{sale.store?.name || 'POS PRO'}</h1>
            <p className="text-xs text-gray-500 print:text-black">{sale.store?.address || 'Store Location Address'}</p>
            <div className="receipt-divider"></div>
          </div>

          {/* Meta Info */}
          <div className="mb-6 space-y-1 text-xs print:text-black">
            <div className="flex justify-between">
              <span>Receipt:</span>
              <span className="font-bold">#{sale.id.toString().split('-')[0].toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span>Date:</span>
              <span>{new Date(sale.createdAt).toLocaleDateString()} {new Date(sale.createdAt).toLocaleTimeString()}</span>
            </div>
            <div className="flex justify-between">
              <span>Staff:</span>
              <span>{sale.user?.name || 'Staff'}</span>
            </div>
            {sale.customer && (
              <div className="flex justify-between">
                <span>Customer:</span>
                <span>{sale.customer.name}</span>
              </div>
            )}
          </div>

          <div className="receipt-divider"></div>

          {/* Items */}
          <div className="mb-6">
            <div className="flex justify-between font-bold mb-2 pb-1 border-b border-gray-100 print:border-black">
              <span className="flex-1">Item</span>
              <span className="w-12 text-center">Qty</span>
              <span className="w-16 text-right">Total</span>
            </div>
            <div className="space-y-2">
              {sale.items.map(i => (
                <div key={i.id} className="text-xs print:text-black">
                  <div className="flex justify-between">
                    <span className="flex-1 pr-2 truncate">{i.product?.name || `Product #${i.productId}`}</span>
                    <span className="w-12 text-center">{i.quantity}</span>
                    <span className="w-16 text-right font-bold">{currencySymbol}{(i.price * i.quantity).toFixed(2)}</span>
                  </div>
                  {i.refundedQuantity > 0 && (
                    <div className="flex justify-between text-rose-500 print:text-black text-[10px] italic">
                      <span className="flex-1 pr-2">↳ Refunded</span>
                      <span className="w-12 text-center">-{i.refundedQuantity}</span>
                      <span className="w-16 text-right">-{currencySymbol}{(i.price * i.refundedQuantity).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="receipt-divider"></div>

          {/* Totals */}
          <div className="space-y-1 mb-8 text-sm print:text-black">
            <div className="flex justify-between text-xs text-gray-600 print:text-black">
              <span>Subtotal</span>
              <span>{currencySymbol}{(sale.total - (sale.tax || 0)).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-600 print:text-black">
              <span>Tax ({taxRatePercent}%)</span>
              <span>{currencySymbol}{(sale.tax || 0).toFixed(2)}</span>
            </div>
            {sale.discount > 0 && (
              <div className="flex justify-between text-xs text-emerald-600 print:text-black">
                <span>Discount</span>
                <span>-{currencySymbol}{sale.discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-black text-lg pt-2 mt-2 border-t border-gray-100 print:border-black">
              <span>TOTAL</span>
              <span>{currencySymbol}{sale.total.toFixed(2)}</span>
            </div>
          </div>

          <div className="receipt-divider"></div>

          {/* Payments */}
          <div className="mb-8 text-xs print:text-black">
            <div className="font-bold mb-1 uppercase tracking-widest text-[10px]">Payment Details</div>
            {sale.payments?.length > 0 ? (
              sale.payments.map((p, i) => (
                <div key={i} className="flex justify-between">
                  <span className="uppercase">{p.method}</span>
                  <span className="font-bold">{currencySymbol}{p.amount.toFixed(2)}</span>
                </div>
              ))
            ) : (
              <div className="flex justify-between">
                <span>CASH</span>
                <span className="font-bold">{currencySymbol}{sale.total.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center text-xs space-y-2 print:text-black">
            <p className="font-black uppercase tracking-widest">Thank you for your visit!</p>
            <p className="text-[10px] text-gray-500 print:text-black italic">Keep this receipt for returns or exchanges.</p>
            <div className="mt-6 flex justify-center scale-90 print:scale-100">
              <Barcode 
                value={sale.id.toString().substring(0, 12).toUpperCase()} 
                width={1.2} 
                height={50} 
                fontSize={10} 
                margin={0} 
                displayValue={true} 
                background="transparent"
              />
            </div>
          </div>

        </div>
      </div>
      {/* Refund Modal */}
      {showRefundModal && (
        <RefundModal 
          sale={sale} 
          onClose={() => setShowRefundModal(false)} 
          onRefunded={fetchSale} 
          currencySymbol={currencySymbol}
        />
      )}
    </div>
  );
}